import React, { useState, useEffect, useRef } from 'react';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import Primary_Logo from "../../assets/Logo/DC_Logo.svg";
import Text_Logo from "../../assets/Logo/Text_logo_primary.svg";

const AnimatedLogo = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const controls = useAnimation();
  const timeoutRef = useRef(null);
  
  useEffect(() => {
    const checkTouchDevice = () => {
      // Consider both tablets and mobile (up to 1024px is typical tablet width)
      setIsTouchDevice(window.innerWidth <= 1024);
    };
    
    checkTouchDevice();
    window.addEventListener('resize', checkTouchDevice);
    
    // Start breathing animation
    controls.start(breatheAnimation.animate);
    
    return () => {
      window.removeEventListener('resize', checkTouchDevice);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [controls]);

  // Pre-attentive processing - Continuous subtle animation
  const breatheAnimation = {
    animate: {
      scale: [1, 1.02, 1],
      y: ["0%", "2%", "0%"],
      transition: {
        duration: 2,
        times: [0, 0.5, 1],
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  // Smooth pursuit motion - Natural eye movement patterns
  const smoothPursuitVariants = {
    initial: { 
      filter: "brightness(1)",
      scale: 1,
      rotate: 0
    },
    hover: {
      scale: 1.15,
      rotate: [0, -5, 0, 5, 0],
      filter: "brightness(1.1)",
      transition: {
        rotate: {
          duration: 2,
          repeat: Infinity,
          ease: "linear"
        },
        scale: {
          duration: 0.3,
          ease: [0.43, 0.13, 0.23, 0.96]
        }
      }
    }
  };

  // Perceptual grouping effect for text logo
  const textLogoVariants = {
    hidden: {
      opacity: 0,
      x: -15,
      filter: "blur(5px)"
    },
    visible: {
      opacity: 1,
      x: 0,
      filter: "blur(0px)",
      transition: {
        duration: 0.5,
        ease: "anticipate"
      }
    }
  };

  // Alpha wave rhythm effect (18Hz simulation)
  const alphaWaveEffect = {
    animate: {
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      borderRadius: "50%",
      scale: [1, 1.2, 1],
      opacity: [0.3, 0.7, 0.3],
      transition: {
        duration: 1/18,
        repeat: Infinity,
        ease: "linear"
      }
    }
  };

  const handleTouchStart = () => {
    if (isTouchDevice) {
      setIsHovered(true);
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Set new timeout for 5s auto-hide
      timeoutRef.current = setTimeout(() => {
        setIsHovered(false);
      }, 5000);
    }
  };

  return (
    <motion.div 
      className="relative flex items-center lg:pl-2 overflow-visible"
      onHoverStart={() => !isTouchDevice && setIsHovered(true)}
      onHoverEnd={() => !isTouchDevice && setIsHovered(false)}
      style={{ perspective: "1000px" }}
    >
      {/* Background alpha wave effect */}
      <motion.div
        className="absolute inset-0 -z-10"
        initial={false}
        animate={isHovered ? alphaWaveEffect.animate : { opacity: 0 }}
      />

      {/* Primary Logo with smooth pursuit motion */}
      <motion.div
        className="relative"
        variants={smoothPursuitVariants}
        initial="initial"
        animate={isHovered ? "hover" : "initial"}
        onTouchStart={handleTouchStart}
      >
        <motion.img 
          src={Primary_Logo}
          alt="DIAA Primary"
          className="h-10 sm:h-10 md:h-12 xl:h-14 object-contain cursor-pointer"
          style={{ 
            transformStyle: "preserve-3d",
            backfaceVisibility: "hidden"
          }}
          animate={breatheAnimation.animate}
        />
      </motion.div>

      {/* Text Logo with perceptual grouping */}
      <AnimatePresence>
        <motion.img 
          src={Text_Logo}
          alt="DIAA"
          className="h-10 sm:h-10 md:h-12 xl:h-14 object-contain ml-3"
          variants={textLogoVariants}
          initial="hidden"
          animate={isHovered ? "visible" : "hidden"}
          style={{ 
            transformStyle: "preserve-3d",
            originX: 0
          }}
        />
      </AnimatePresence>
    </motion.div>
  );
};

export default AnimatedLogo;
